import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Building2, Users, Handshake } from 'lucide-react';

export function AlliancesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="alianzas" 
      className="py-24 px-4 sm:px-6 lg:px-8 bg-black"
      ref={ref}
      data-testid="alliances-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="text-orange-500 font-semibold tracking-widest text-sm">TRABAJO CONJUNTO</span>
          <h2 
            className="text-4xl sm:text-5xl font-extrabold text-white mt-2 mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="alliances-title"
          >
            ALIANZAS INSTITUCIONALES
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Trabajamos de la mano con instituciones comprometidas con el desarrollo juvenil.
          </p>
        </motion.div>

        {/* Main Alliance Card */}
        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-white/10 rounded-2xl p-8 md:p-12 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-500/5 rounded-full blur-3xl" />
            
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center">
                  <Building2 className="text-white" size={48} />
                </div>
              </div>
              
              {/* Content */}
              <div className="text-center md:text-left">
                <h3 
                  className="text-2xl md:text-3xl font-bold text-white mb-4"
                  style={{ fontFamily: 'Barlow Condensed' }}
                  data-testid="alliance-title"
                >
                  PLATAFORMA MUNICIPAL DE JUVENTUD
                </h3>
                <p className="text-zinc-400 mb-6 leading-relaxed">
                  Como organización práctica vinculada a la Plataforma Municipal de Juventud de Sesquilé, 
                  Jotuns Basketball Club participa activamente en la construcción de políticas públicas 
                  de juventud y el fortalecimiento del tejido social juvenil en el municipio.
                </p>
                <div className="flex flex-wrap justify-center md:justify-start gap-4">
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Users className="text-orange-500" size={18} />
                    <span>Participación Juvenil</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Handshake className="text-orange-500" size={18} />
                    <span>Trabajo Colaborativo</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Supporting Text */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="text-zinc-500 max-w-2xl mx-auto">
            Agradecemos el apoyo de la <strong className="text-zinc-300">Alcaldía de Sesquilé</strong>, 
            la <strong className="text-zinc-300">Secretaría de Desarrollo Social</strong> y todas las 
            instituciones que creen en el poder transformador del deporte juvenil.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {[
            { value: '2019', label: 'Año de Fundación' },
            { value: '50+', label: 'Jóvenes Beneficiados' },
            { value: '100+', label: 'Entrenamientos' },
            { value: '10+', label: 'Eventos Realizados' }
          ].map((stat, index) => (
            <div 
              key={stat.label}
              className="text-center p-6 bg-zinc-900/30 rounded-xl border border-white/5"
              data-testid={`stat-${index}`}
            >
              <div className="text-3xl md:text-4xl font-bold text-orange-500 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                {stat.value}
              </div>
              <div className="text-sm text-zinc-500">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
